import { zodResolver } from '@hookform/resolvers/zod'
import { useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod'
import { Skeleton } from '@/components/ui/skeleton'
import { Button, buttonVariants } from '@/components/ui/button'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { Plus, Trash2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { useWelcomeMessages } from '@/hooks/courses/welcomeMessages/useWelcomeMessages'
import DeleteAlertDialog from '@/components/DeleteAlertDialog'
import { useEffect, useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'

export const welcomeMessageSchema = z.object({
  title: z.string().min(3).max(50),
  subtitle: z.string().min(0).max(100).optional(),
  content: z.string().min(5).max(5000),
})

const formSchema = z.object({
  messages: z.array(welcomeMessageSchema).min(1).max(3),
})

type WelcomeMessagesProps = Readonly<{
  courseName: string
  closeDialog: () => void
}>

export default function WelcomeMessagesForm({ courseName, closeDialog }: WelcomeMessagesProps) {
  const { get: currentWelcomeMessages, set: setWelcomeMessages, deleteAll } = useWelcomeMessages()
  const { data: welcomeMessage, isLoading, isPending, isError } = currentWelcomeMessages(courseName)
  const [selectedMessage, setSelectedMessage] = useState<string>('message-0')

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      messages: [{ title: '', subtitle: '', content: '' }],
    },
    mode: 'onTouched',
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'messages',
    rules: { maxLength: 3 },
  })

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    setWelcomeMessages.mutate({ courseName, messages: data.messages })
    closeDialog()
  }

  const onAddMessage = () => {
    append({ title: '', subtitle: '', content: '' })
    setSelectedMessage(`message-${fields.length}`)
  }

  const onRemoveMessage = (index: number) => {
    remove(index)
    setSelectedMessage(`message-${index - 1}`)
  }

  useEffect(() => {
    if (welcomeMessage && welcomeMessage.length > 0) {
      form.reset({
        messages: welcomeMessage,
      })
    }
  }, [welcomeMessage, form])

  if (isLoading || isPending) return <Skeleton className="h-[23rem] w-full" />
  if (isError) return <p>Error Getting the messages for {courseName}</p>

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Accordion
          type="single"
          collapsible
          value={selectedMessage}
          onValueChange={setSelectedMessage}
          className="w-full"
        >
          {fields.map((field, index) => (
            <AccordionItem key={field.id} value={`message-${index}`}>
              <div className="flex items-center gap-2">
                <AccordionTrigger
                  value={`message-${index}`}
                  onClick={(e) => {
                    setSelectedMessage(e.currentTarget.value)
                  }}
                  className="flex-1"
                >
                  Message {index + 1}
                </AccordionTrigger>
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="mr-4"
                    onClick={() => onRemoveMessage(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <AccordionContent>
                <Card className="p-4">
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name={`messages.${index}.title`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Title</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`messages.${index}.subtitle`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Subtitle</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`messages.${index}.content`}
                      render={({ field }) => {
                        return (
                          <FormItem>
                            <FormLabel>Content</FormLabel>
                            <Tabs defaultValue="edit" className="w-full">
                              <TabsList className="mb-2">
                                <TabsTrigger value="edit">Edit</TabsTrigger>
                                <TabsTrigger value="preview">Preview</TabsTrigger>
                              </TabsList>
                              <TabsContent value="edit">
                                <FormControl>
                                  <Textarea
                                    spellCheck
                                    className="h-[10rem] font-mono"
                                    placeholder="You can use Markdown formatting here..."
                                    {...field}
                                  />
                                </FormControl>
                              </TabsContent>
                              <TabsContent value="preview">
                                <div className="markdown-content border rounded-md p-4 h-[10rem] overflow-y-auto bg-background">
                                  {field.value ? (
                                    <Markdown
                                      components={{
                                        // Custom paragraph component with added margin
                                        a: ({ node, ...props }) => <a className="text-primary underline" {...props} />,
                                      }}
                                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                                      remarkPlugins={[remarkGfm]}
                                    >
                                      {field.value}
                                    </Markdown>
                                  ) : (
                                    <p className="text-muted-foreground">Nothing to preview</p>
                                  )}
                                </div>
                              </TabsContent>
                            </Tabs>
                            <FormDescription className="text-xs mt-2">
                              Supports Markdown: **bold**, *italic*, [links](https://example.com), lists, line break
                              &quot;\&quot;, etc.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )
                      }}
                    />
                  </CardContent>
                </Card>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="flex justify-center gap-4">
          {fields.length < 3 && (
            <Button type="button" variant="default" size="sm" onClick={onAddMessage}>
              <Plus className="h-4 w-4 mr-2" />
              Add Message
            </Button>
          )}
          <DeleteAlertDialog
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
            title="Delete All Messages"
            btnText="Delete All"
            description="Are you sure you want to delete all welcome messages? this action cannot be undone"
            handleOnClick={() => deleteAll.mutate({ courseName })}
          />
        </div>

        <div className="flex flex-col">
          <Separator className="mb-3" />
          <div className="flex flex-row gap-3">
            <Button type="submit" disabled={!form.formState.isValid} className="w-full">
              Save
            </Button>
            <Button type="reset" className="w-full" variant="outline" onClick={() => form.reset()}>
              Reset
            </Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
